import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  ArrowRight,
  Ban,
  Download,
  GitMerge,
  ListChecks,
  RefreshCcw,
  Save,
  Scissors,
  Sparkles,
} from 'lucide-react';
import { getDesktopDownloadUrl } from '@shared';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { fetchGraphQL } from '@/relay/fetchGraphQL';
import { FadeIn } from '@/components/animations/FadeIn';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { DaySheetlyInput } from '@/components/fields/DaySheetlyInput';
import { DaySheetlySelect } from '@/components/fields/DaySheetlySelect';
import { getActivityBridge } from '../activityAssistant.service';
import {
  durationMinutes,
  formatActivityTime,
  formatTaskDuration,
  mergeActivityDrafts,
  splitActivityDraft,
  taskDateForDraft,
  toActivityDraft,
} from '../activityAssistant.model';
import type {
  ActivityBridge,
  ActivityDraft,
  ActivityRule,
  ActivityStatus,
} from '../activityAssistant.types';

interface ProjectOption {
  id: string;
  name: string;
  color?: string | null;
  hourlyRate: number;
  currency: 'BRL' | 'USD';
}

interface MemberOption {
  id: string;
  name: string;
  email?: string;
}

interface WorkspaceResponse {
  data?: {
    projects: Array<{
      id: string;
      name: string;
      color?: string | null;
      hourlyRate: number;
      currency: 'BRL' | 'USD';
    }>;
    viewer: null | {
      id: string;
      activeWorkspaceId: string | null;
      workspaces: Array<{
        id: string;
        members: Array<{
          user: { id: string; name: string; email: string };
        }>;
      }>;
    };
  };
  errors?: Array<{ message: string }>;
}

interface CreateTaskResponse {
  data?: { createTask?: { id: string } };
  errors?: Array<{ message: string }>;
}

const workspaceQuery = `
  query ActivityReviewWorkspace {
    projects {
      id
      name
      color
      hourlyRate
      currency
    }
    viewer {
      id
      activeWorkspaceId
      workspaces {
        id
        members {
          user { id name email }
        }
      }
    }
  }
`;

const createTaskMutation = `
  mutation ActivityReviewCreateTask(
    $title: String!
    $date: String!
    $duration: String!
    $projectId: ID
    $hourlyRate: Float
    $currency: Currency
    $billingStatus: BillingStatus
    $memberIds: [ID!]
  ) {
    createTask(
      title: $title
      date: $date
      duration: $duration
      projectId: $projectId
      hourlyRate: $hourlyRate
      currency: $currency
      billingStatus: $billingStatus
      memberIds: $memberIds
    ) { id }
  }
`;

function toDateTimeLocal(value: string): string {
  return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
}

function fromDateTimeLocal(value: string, fallback: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString();
}

export function ActivityReviewPage({
  bridge = getActivityBridge(),
}: {
  bridge?: ActivityBridge;
}) {
  const { user } = useAuth();
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [status, setStatus] = useState<ActivityStatus | null>(null);
  const [drafts, setDrafts] = useState<ActivityDraft[]>([]);
  const [rules, setRules] = useState<ActivityRule[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const nextStatus = await bridge.getStatus();
      setStatus(nextStatus);
      if (nextStatus.environment === 'web') {
        setDrafts([]);
        return;
      }

      const [segments, nextRules, workspace] = await Promise.all([
        bridge.listSegments({ date, userId: user.id }),
        bridge.listRules(),
        fetchGraphQL({ text: workspaceQuery } as never, {}) as Promise<WorkspaceResponse>,
      ]);
      if (workspace.errors?.[0]) throw new Error(workspace.errors[0].message);

      const viewer = workspace.data?.viewer;
      const activeWorkspace = viewer?.workspaces.find(
        (item) => item.id === viewer.activeWorkspaceId,
      );
      setProjects((workspace.data?.projects ?? []) as ProjectOption[]);
      setMembers(
        activeWorkspace?.members.map((item) => ({
          id: item.user.id,
          name: item.user.name,
          email: item.user.email,
        })) ?? [{ id: user.id, name: user.name, email: user.email }],
      );
      setRules(nextRules);
      setDrafts(
        segments.map((segment) => ({
          ...toActivityDraft(segment, nextRules),
          memberIds: [user.id],
        })),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load local activities');
    } finally {
      setLoading(false);
    }
  }, [bridge, date, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const createTask = async (draft: ActivityDraft): Promise<string> => {
    const project = projects.find((item) => item.id === draft.projectId);
    const response = (await fetchGraphQL(
      { text: createTaskMutation } as never,
      {
        title: draft.title.trim(),
        date: taskDateForDraft(draft),
        duration: formatTaskDuration(durationMinutes(draft.startedAt, draft.endedAt)),
        projectId: project?.id || null,
        hourlyRate: project?.hourlyRate ?? 0,
        currency: project?.currency ?? 'BRL',
        billingStatus: 'unbilled',
        memberIds: draft.memberIds,
      },
    )) as CreateTaskResponse;
    if (response.errors?.[0]) throw new Error(response.errors[0].message);
    const taskId = response.data?.createTask?.id;
    if (!taskId) throw new Error('The server did not return the created task');
    return taskId;
  };

  if (!user) return null;

  return (
    <ActivityReviewContent
      bridge={bridge}
      status={status}
      date={date}
      onDateChange={setDate}
      drafts={drafts}
      onDraftsChange={setDrafts}
      rules={rules}
      onRulesChange={setRules}
      projects={projects}
      members={members}
      loading={loading}
      onReload={load}
      onCreateTask={createTask}
    />
  );
}

export function ActivityReviewContent({
  bridge,
  status,
  date,
  onDateChange,
  drafts,
  onDraftsChange,
  rules,
  onRulesChange,
  projects,
  members,
  loading,
  onReload,
  onCreateTask,
}: {
  bridge: ActivityBridge;
  status: ActivityStatus | null;
  date: string;
  onDateChange: (date: string) => void;
  drafts: ActivityDraft[];
  onDraftsChange: (drafts: ActivityDraft[]) => void;
  rules: ActivityRule[];
  onRulesChange: (rules: ActivityRule[]) => void;
  projects: ProjectOption[];
  members: MemberOption[];
  loading: boolean;
  onReload: () => Promise<void>;
  onCreateTask: (draft: ActivityDraft) => Promise<string>;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const selected = drafts.filter(
    (draft) => draft.selected && !draft.ignored && !draft.importedTaskId,
  );
  const importable = selected.filter((draft) => draft.title.trim().length > 0);

  const updateDraft = (id: string, changes: Partial<ActivityDraft>) => {
    onDraftsChange(
      drafts.map((draft) => (draft.id === id ? { ...draft, ...changes } : draft)),
    );
  };

  const mergeSelected = () => {
    try {
      const merged = mergeActivityDrafts(selected);
      const selectedIds = new Set(selected.map((draft) => draft.id));
      const insertionIndex = drafts.findIndex((draft) => selectedIds.has(draft.id));
      const next = drafts.filter((draft) => !selectedIds.has(draft.id));
      next.splice(Math.max(0, insertionIndex), 0, merged);
      onDraftsChange(next);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not merge activities');
    }
  };

  const splitSelected = () => {
    if (selected.length !== 1) {
      toast.error('Select exactly one activity to split');
      return;
    }
    try {
      const [first, second] = splitActivityDraft(selected[0]);
      const index = drafts.findIndex((draft) => draft.id === selected[0].id);
      const next = drafts.filter((draft) => draft.id !== selected[0].id);
      next.splice(Math.max(0, index), 0, first, second);
      onDraftsChange(next);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not split activity');
    }
  };

  const ignoreSelected = async () => {
    const sourceIds = Array.from(new Set(selected.flatMap((draft) => draft.sourceSegmentIds)));
    try {
      await Promise.all(sourceIds.map((id) => bridge.updateSegment(id, { ignored: true })));
      onDraftsChange(
        drafts.map((draft) =>
          draft.sourceSegmentIds.some((id) => sourceIds.includes(id))
            ? { ...draft, ignored: true, selected: false }
            : draft,
        ),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not ignore activities');
    }
  };

  const excludeApplication = async (draft: ActivityDraft) => {
    try {
      const settings = await bridge.getSettings();
      const app = draft.processName.toLocaleLowerCase();
      if (!settings.excludedApps.includes(app)) {
        await bridge.updateSettings({ excludedApps: [...settings.excludedApps, app] });
      }
      await Promise.all(
        draft.sourceSegmentIds.map((id) => bridge.updateSegment(id, { ignored: true })),
      );
      onDraftsChange(
        drafts.map((item) =>
          item.processName === draft.processName
            ? { ...item, ignored: true, selected: false }
            : item,
        ),
      );
      toast.success(`${draft.applicationName} excluded on this device`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not exclude application');
    }
  };

  const rememberAssociation = async (draft: ActivityDraft) => {
    if (!draft.projectId) {
      toast.error('Select a project before saving an association');
      return;
    }
    try {
      const rule = await bridge.saveRule({
        processName: draft.processName,
        titleContains: draft.windowTitle,
        projectId: draft.projectId,
        suggestedTitle: draft.title,
      });
      onRulesChange([...rules.filter((item) => item.id !== rule.id), rule]);
      updateDraft(draft.id, { suggestedByRuleId: rule.id });
      toast.success('Association saved locally');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save association');
    }
  };

  const importSelected = async () => {
    setImporting(true);
    const results = new Map<string, string>();
    const failures: string[] = [];
    try {
      for (const draft of importable) {
        try {
          const taskId = await onCreateTask(draft);
          results.set(draft.id, taskId);
        } catch (error) {
          failures.push(
            `${draft.title}: ${error instanceof Error ? error.message : 'unknown error'}`,
          );
        }
      }

      const nextDrafts = drafts.map((draft) => {
        const taskId = results.get(draft.id);
        return taskId ? { ...draft, selected: false, importedTaskId: taskId } : draft;
      });
      onDraftsChange(nextDrafts);

      const allSources = Array.from(
        new Set(importable.flatMap((draft) => draft.sourceSegmentIds)),
      );
      for (const sourceId of allSources) {
        const relatedDrafts = drafts.filter((draft) => draft.sourceSegmentIds.includes(sourceId));
        const everyRelatedDraftSucceeded = relatedDrafts.every(
          (draft) => Boolean(draft.importedTaskId) || results.has(draft.id),
        );
        if (everyRelatedDraftSucceeded) {
          const taskId = relatedDrafts
            .map((draft) => results.get(draft.id) || draft.importedTaskId)
            .find(Boolean);
          if (taskId) {
            try {
              await bridge.markSegmentImported(sourceId, taskId);
            } catch (error) {
              failures.push(
                `Local import marker: ${
                  error instanceof Error ? error.message : 'unknown error'
                }`,
              );
            }
          }
        }
      }
    } finally {
      setImporting(false);
      setConfirmOpen(false);
    }

    if (results.size > 0) {
      toast.success(`${results.size} task${results.size === 1 ? '' : 's'} added`);
    }
    if (failures.length > 0) {
      toast.error(
        `${failures.length} item${failures.length === 1 ? '' : 's'} failed. You can retry them.`,
      );
    }
  };

  if (status?.environment === 'web') {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Review today is available in the desktop app</CardTitle>
            <CardDescription>
              The web app never reads activity from other applications on your computer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href={getDesktopDownloadUrl(process.env.DAYSHEETLY_WEBSITE_URL)}>
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Download desktop for Windows or Linux
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col gap-5 px-6 py-6">
      <FadeIn>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Review activities</h1>
            <p className="text-sm text-muted-foreground">
              Nothing is sent until you confirm the tasks below.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <DaySheetlyInput
              type="date"
              label="Day"
              value={date}
              onValueChange={(value) => onDateChange(String(value || date))}
              testId="activity-review-date"
            />
            <Button type="button" variant="outline" size="icon" onClick={() => void onReload()} aria-label="Reload activities">
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.05}>
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-background p-3">
          <Button type="button" size="sm" variant="outline" onClick={mergeSelected} disabled={selected.length < 2}>
            <GitMerge className="mr-2 h-4 w-4" /> Merge selected
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={splitSelected} disabled={selected.length !== 1}>
            <Scissors className="mr-2 h-4 w-4" /> Split selected
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => void ignoreSelected()} disabled={selected.length === 0}>
            <Ban className="mr-2 h-4 w-4" /> Ignore selected
          </Button>
          <span className="ml-auto text-sm text-muted-foreground">{selected.length} selected</span>
          <Button type="button" size="sm" onClick={() => setConfirmOpen(true)} disabled={importable.length === 0}>
            <ListChecks className="mr-2 h-4 w-4" /> Add selected to DaySheetly
          </Button>
        </div>
      </FadeIn>

      {loading ? (
        <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">Loading local activities…</div>
      ) : drafts.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <p className="font-medium">No local activities for this day</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Enable the assistant and keep DaySheetly running to build a private timeline.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="activity-review-list">
          {drafts.map((draft) => (
            <ActivityDraftCard
              key={draft.id}
              draft={draft}
              projects={projects}
              members={members}
              onChange={(changes) => updateDraft(draft.id, changes)}
              onExclude={() => void excludeApplication(draft)}
              onRemember={() => void rememberAssociation(draft)}
            />
          ))}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {importable.length} task{importable.length === 1 ? '' : 's'} to DaySheetly?</DialogTitle>
            <DialogDescription>
              Only the title, date, duration, selected project and members are sent. Raw window
              activity remains on this device.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {importable.map((draft) => (
              <div key={draft.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                <span className="truncate pr-4">{draft.title}</span>
                <span className="shrink-0 text-muted-foreground">
                  {formatTaskDuration(durationMinutes(draft.startedAt, draft.endedAt))}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)} disabled={importing}>Cancel</Button>
            <Button type="button" onClick={() => void importSelected()} disabled={importing}>
              {importing ? 'Adding tasks…' : 'Confirm and add tasks'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityDraftCard({
  draft,
  projects,
  members,
  onChange,
  onExclude,
  onRemember,
}: {
  draft: ActivityDraft;
  projects: ProjectOption[];
  members: MemberOption[];
  onChange: (changes: Partial<ActivityDraft>) => void;
  onExclude: () => void;
  onRemember: () => void;
}) {
  const unavailable = draft.ignored || Boolean(draft.importedTaskId);
  const projectOptions = [
    { value: '__none__', label: 'No project' },
    ...projects.map((project) => ({ value: project.id, label: project.name })),
  ];
  const memberOptions = members.map((member) => ({ value: member.id, label: member.name }));

  return (
    <Card className={unavailable ? 'opacity-60' : undefined} data-testid={`activity-draft-${draft.id}`}>
      <CardContent className="grid gap-4 p-4 lg:grid-cols-[auto_minmax(0,1.2fr)_minmax(13rem,0.8fr)]">
        <div className="pt-1">
          <Checkbox
            checked={draft.selected}
            disabled={unavailable}
            onCheckedChange={(checked) => onChange({ selected: Boolean(checked) })}
            aria-label={`Select ${draft.title}`}
          />
        </div>
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-medium">{formatActivityTime(draft.startedAt)} <ArrowRight className="mx-1 inline h-3 w-3" /> {formatActivityTime(draft.endedAt)}</span>
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{draft.applicationName}</span>
            <span className="text-xs text-muted-foreground">{formatTaskDuration(durationMinutes(draft.startedAt, draft.endedAt))}</span>
            {draft.importedTaskId ? <span className="text-xs font-medium text-emerald-600">Imported</span> : null}
            {draft.ignored ? <span className="text-xs font-medium text-muted-foreground">Ignored</span> : null}
          </div>
          <DaySheetlyInput
            label="Task title"
            value={draft.title}
            disabled={unavailable}
            onValueChange={(value) => onChange({ title: String(value ?? '') })}
            testId={`activity-title-${draft.id}`}
          />
          <p className="truncate text-xs text-muted-foreground" title={draft.windowTitle}>
            Source: {draft.windowTitle || draft.processName}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <DaySheetlyInput
              type="datetime-local"
              label="Start"
              value={toDateTimeLocal(draft.startedAt)}
              disabled={unavailable}
              onValueChange={(value) =>
                onChange({ startedAt: fromDateTimeLocal(String(value), draft.startedAt) })
              }
            />
            <DaySheetlyInput
              type="datetime-local"
              label="End"
              value={toDateTimeLocal(draft.endedAt)}
              disabled={unavailable}
              onValueChange={(value) =>
                onChange({ endedAt: fromDateTimeLocal(String(value), draft.endedAt) })
              }
            />
          </div>
        </div>
        <div className="space-y-3">
          <DaySheetlySelect
            label="Project"
            value={draft.projectId || '__none__'}
            disabled={unavailable}
            onValueChange={(value) => onChange({ projectId: value === '__none__' ? '' : value || '' })}
            options={projectOptions}
            testId={`activity-project-${draft.id}`}
          />
          <DaySheetlySelect
            mode="multiple"
            label="Members"
            helperText="Up to 2 members"
            value={draft.memberIds}
            disabled={unavailable}
            onValueChange={(value) => onChange({ memberIds: value.slice(0, 2) })}
            options={memberOptions}
            testId={`activity-members-${draft.id}`}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" disabled={unavailable || !draft.projectId} onClick={onRemember}>
              {draft.suggestedByRuleId ? <Save className="mr-2 h-3.5 w-3.5" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
              {draft.suggestedByRuleId ? 'Association saved' : 'Remember association'}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={unavailable} onClick={onExclude}>
              <Ban className="mr-2 h-3.5 w-3.5" /> Exclude app
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
